import { useMemo, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { corePdpLink } from 'components/helpers/links';
import { buildCarouselPhotos } from 'components/marketplace/helpers/galleryPhotos';
import {
  buildPropertyAnalyticsValue,
  PropertyAnalytics,
} from 'components/marketplace/helpers/useTrackProperty';
import { PhotoCarousel as MapPhotoCarousel } from 'components/marketplace/map/PhotoCarousel';
import {
  heroWalkthroughVideoUrl,
  pdpHeroMedia,
  pdpMediaAvailability,
} from 'components/marketplace/property/media/pdpEnhancedMedia';
import { usePdpMediaSlot } from 'components/marketplace/property/media/usePdpMediaSlot';
import { MiniMapPropertySummary } from 'components/marketplace/property/PropertySection/MiniMapPropertySummary';
import { ShareModal } from 'components/marketplace/property/ShareModal';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import {
  streetViewAltText,
  streetViewLocationFor,
  StreetViewPhoto,
} from 'components/marketplace/shared/StreetViewPhoto';

interface MiniPropertyDetailPageProps {
  property: MarketplaceProperty;
  onClose: () => void;
}

export const MobileMiniPropertyDetailPage = ({
  property,
  onClose,
}: MiniPropertyDetailPageProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const handleToggleShareModal = () => {
    setIsShareModalOpen((prev) => !prev);
  };

  const propertyLink = corePdpLink(property.slug, property.addressToken);

  const handleOnClick = () => {
    window.location.href = getCustomerNavigationHref(propertyLink);
  };

  // Floor plans intentionally omitted here: the PhotoCarousel cover-crops
  // portrait plans. Tapping a photo navigates to the full PDP, where floor
  // plans are rendered uncropped in the gallery and Lightbox.
  const carouselPhotos = buildCarouselPhotos(property);
  const analyticsValue = useMemo(
    () => buildPropertyAnalyticsValue(property, 'map-drawer'),
    [property],
  );
  const streetViewLocation = useMemo(
    () =>
      streetViewLocationFor({
        availableSoon: property.listingState === 'AVAILABLE_SOON',
        photoCount: carouselPhotos.length,
        latitude: property.latitude,
        longitude: property.longitude,
      }),
    [property, carouselPhotos.length],
  );
  // A home painting the street-view fallback does not enter an enhanced-media
  // experiment because the fallback owns the same hero surface.
  const mediaAvailability = useMemo(
    () =>
      pdpMediaAvailability(
        streetViewLocation == null ? heroWalkthroughVideoUrl(property) : null,
        streetViewLocation == null ? property.tour3d : null,
      ),
    [property, streetViewLocation],
  );
  const mediaSlot = usePdpMediaSlot({
    addressToken: property.addressToken,
    availability: mediaAvailability,
    surface: 'map-drawer',
  });
  const heroMedia = pdpHeroMedia(property, mediaSlot, 'control');

  return (
    <PropertyAnalytics.Provider value={analyticsValue}>
      <Col className="h-full w-full" key={`mobile-mini-pdp-${property?.addressToken}`}>
        <Box className="shrink-0 pb-6">
          <MapPhotoCarousel
            key={`mobile-carousel-${property?.addressToken}`}
            photos={carouselPhotos}
            onClose={onClose}
            onOpenShareModal={handleToggleShareModal}
            isRounded
            onPhotoClick={handleOnClick}
            heroOverlay={heroMedia}
            emptyFallback={
              streetViewLocation ? (
                <StreetViewPhoto
                  latitude={streetViewLocation.latitude}
                  longitude={streetViewLocation.longitude}
                  alt={streetViewAltText(property)}
                  fallbackLabel="Photos coming soon"
                  className="h-full w-full"
                />
              ) : null
            }
          />
        </Box>
        <Col
          data-mini-pdp-scroll
          className="min-h-0 flex-1 touch-pan-y gap-2.5 overflow-y-auto px-5"
        >
          <MiniMapPropertySummary property={property} />
          <Button
            analyticsName="mobile-map-pdp-link"
            href={propertyLink}
            variant="secondary"
            aria-label="View home"
            className="w-full"
          >
            View home
          </Button>
        </Col>
        <ShareModal
          property={property}
          isOpen={isShareModalOpen}
          onClose={handleToggleShareModal}
        />
      </Col>
    </PropertyAnalytics.Provider>
  );
};
